import type { ReactElement } from 'react';
import type { DevicePersonIdentity } from '@kirin/api-client/contracts/device-inventory';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  GUEST_DESCRIPTION,
  GUEST_LABEL,
  IDENTITY_DESCRIPTIONS,
  IDENTITY_LABELS,
} from './constants';
import { personInitials } from './deviceDisplay';

/** "Shared" when more than one person was seen in 30 days, else "Single user". */
export function SharedBadge({
  shared,
  className,
}: {
  shared: boolean;
  className?: string;
}): ReactElement {
  return (
    <span
      data-testid="device-shared-badge"
      data-shared={shared}
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold',
        shared
          ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
          : 'border-border bg-muted text-muted-foreground',
        className
      )}
    >
      {shared ? 'Shared' : 'Single user'}
    </span>
  );
}

/** KIR-5658: an admin revoked this enrolled machine. */
export function RevokedBadge(): ReactElement {
  return (
    <span
      data-testid="device-revoked-badge"
      className="inline-flex items-center whitespace-nowrap rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
    >
      Revoked
    </span>
  );
}

const IDENTITY_STYLES: Record<DevicePersonIdentity, string> = {
  member:
    'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  discovered:
    'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  unattributed: 'border-border bg-muted text-muted-foreground',
  offboarded:
    'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300',
};

/**
 * Who a person seen on an endpoint is: Member / Discovered / Unattributed /
 * Offboarded, or Guest for the machine's shared Guest / kiosk sessions.
 */
export function IdentityBadge({
  identity,
  guest = false,
}: {
  identity: DevicePersonIdentity;
  guest?: boolean;
}): ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-testid="device-identity-badge"
          data-identity={identity}
          data-guest={guest}
          className={cn(
            'inline-flex cursor-help items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold',
            IDENTITY_STYLES[identity]
          )}
        >
          {guest ? GUEST_LABEL : IDENTITY_LABELS[identity]}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-60">
        {guest ? GUEST_DESCRIPTION : IDENTITY_DESCRIPTIONS[identity]}
      </TooltipContent>
    </Tooltip>
  );
}

const AVATAR_TONES = [
  'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
];

function toneFor(seed: string): string {
  let hash = 0;
  for (const ch of seed) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_TONES[hash % AVATAR_TONES.length];
}

/** Initials in a circle, coloured by the person's id; grey for an unknown person. */
export function PersonAvatar({
  label,
  seed,
  muted = false,
  className,
}: {
  label: string;
  seed: string;
  muted?: boolean;
  className?: string;
}): ReactElement {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ring-2 ring-background',
        muted ? 'bg-muted text-muted-foreground' : toneFor(seed),
        className
      )}
    >
      {personInitials(label)}
    </span>
  );
}
